import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Phone, ArrowLeft, MapPin, User, Building2, FileText, Calendar, Globe, Target, Award, GraduationCap, FileCheck, DollarSign, Clock, Info } from "lucide-react";
import { coachData } from "../../base-component/Data";
import moment from "moment";
import { userDetailApi } from "../../Services/ApiServices";
import { IMG_URL } from "../../Services/Api";
import dummy from "../../assets/Images/dummy.jpeg";
import toast from "react-hot-toast";

const DetailField = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
    <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-2 uppercase tracking-wide">
      {Icon && <Icon size={14} className="text-gray-400" />}
      {label}
    </p>
    <p className="text-sm font-semibold text-gray-900 leading-relaxed">{value || "—"}</p>
  </div>
);

const CoachDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [coach, setCoach] = useState(null);

  useEffect(() => {
    userDetailApi({
      user_id: id
    })
      .then(res => {
        console.log(res.data.data);
        setCoach(res.data.data);
      })
      .catch(err => {
        console.log(err);
        setCoach(null);
      });
  }, [id]);

  if (!coach) return <div className="p-10 text-center text-gray-600">Loading coach...</div>;

  return (
    <div>
      {/* Header with Back Button */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200 border border-gray-200"
        >
          <ArrowLeft size={18} className="text-gray-700" />
        </button>
        <h1 className="text-xl font-bold text-gray-900 tracking-tight">Coach Details</h1>
      </div>

      {/* Personal Information Card - Horizontal Layout */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 mb-6 relative overflow-hidden">
        {/* Horizontal Layout with Profile and Details */}
        <div className="flex flex-col md:flex-row items-start md:items-start gap-6">
          {/* Left Side - Profile Picture */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden border-2 border-white shadow-md">
              {coach.profile_image ? (
                <img
                  src={IMG_URL + coach.profile_image || dummy}
                  alt={`${coach.firstName} ${coach.lastName}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <User size={40} className="text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Name and Details Grid */}
          <div className="flex-1 w-full">
            {/* Name */}
            <h2 className="text-lg font-bold text-gray-900 capitalize mb-4 tracking-tight">
              {coach.firstname} {coach.lastname}
            </h2>

            {/* Details in Individual Boxes */}
            <div className="space-y-4">
              {/* Top Row - 4 Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <DetailField label="Phone" value={ coach.country_code + " " + coach.phone_number} icon={Phone} />
                <DetailField label="Country" value={coach.country} icon={MapPin} />
                <DetailField label="City" value={coach.city} icon={Building2} />
                <DetailField
                  label="Coaching Since"
                  value={coach.coaching_start_date ? moment(coach.coaching_start_date).format("D MMMM, YYYY") : null}
                  icon={Calendar}
                />
              </div>

              {/* Professional Background and Created At */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-8">
                  <DetailField label="Professional Background" value={coach.professional_background} icon={FileText} />
                </div>
                <div className="md:col-span-4">
                  <DetailField
                    label="Created At"
                    value={coach.createdAt || "—"}
                    icon={Calendar}
                  />
                </div>
              </div>

              {/* Languages */}
              <div className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
                <h3 className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-2 uppercase tracking-wide">
                  <Globe size={14} className="text-gray-400" />
                  Languages
                </h3>
                <div className="flex flex-wrap gap-3">
                  {coach.language_list && coach.language_list.length > 0 ? (
                    coach.language_list.map((item, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-200 transition-all duration-200"
                      >
                        {item?.language?.language}
                      </span>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 italic">No languages specified</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Coaching Details - Grid Layout */}
      <div className="space-y-6 mb-6">

        {/* Coaching Specialities and Accreditation - 6-6 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Coaching Specialities */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center">
                <Target size={18} className="text-green-600" />
              </div>
              <span>Coaching Specialities</span>
            </h3>
            <div className="flex flex-wrap gap-3">
              {coach.specialities_list && coach.specialities_list.length > 0 ? (
                coach.specialities_list.map((speciality, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-green-100 bg-green-50 text-green-700 hover:bg-green-100 hover:border-green-200 transition-all duration-200"
                  >
                    {speciality?.specialities?.specialities}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No specialities specified</p>
              )}
            </div>
          </div>

          {/* Coaching Accreditation */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                <Award size={18} className="text-purple-600" />
              </div>
              <span>Coaching Accreditation</span>
            </h3>
            <div className="flex flex-wrap gap-3">
              {coach.accreditations_list && coach.accreditations_list.length > 0 ? (
                coach.accreditations_list.map((accreditation, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-purple-100 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-200 transition-all duration-200"
                  >
                    {accreditation?.accreditations?.accreditation}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No accreditation specified</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information - Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6">
        {/* Coaching Institute - 6 columns */}
        <div className="md:col-span-6 rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
              <GraduationCap size={18} className="text-amber-600" />
            </div>
            <span>Coaching Institute</span>
          </h3>
          <p className="text-sm font-semibold text-gray-900 leading-relaxed">{coach.coaching_institute || "—"}</p>
        </div>

        {/* Rates Section - 4 columns */}
        <div className="md:col-span-4 rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center">
              <DollarSign size={18} className="text-orange-600" />
            </div>
            <span>Rates</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                Currency
              </p>
              <p className="text-sm font-semibold text-gray-900">
                {coach?.currency_list?.length > 0
                  ? coach.currency_list[0]?.currency?.currency
                  : "—"}
              </p>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Minimum Rate</p>
              <p className="text-sm font-semibold text-gray-900">
                {coach.currency_maximum_rate ? `${coach.currency_maximum_rate}` : "—"}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Maximum Rate</p>
              <p className="text-sm font-semibold text-gray-900">
                {coach.currency_minimum_rate ? `${coach.currency_minimum_rate}` : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Certificate - 2 columns */}
        <div className="md:col-span-2 rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
              <FileCheck size={18} className="text-teal-600" />
            </div>
            <span>Certificate</span>
          </h3>
          {coach.certificate ? (
            <a
              href={IMG_URL + coach.certificate || dummy}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 rounded-lg text-sm font-semibold bg-teal-50 text-teal-700 border-2 border-teal-100 hover:bg-teal-100 hover:border-teal-200 transition-all duration-200" style={{ backgroundImage: `url(${IMG_URL + coach.certificate})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
              View Certificate
            </a>
          ) : (
            <p className="text-sm text-gray-500 italic">No certificate available</p>
          )}
        </div>
      </div>

      {/* Availability Section */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6">
        <h3 className="text-base font-bold text-gray-900 mb-6 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <Clock size={18} className="text-blue-600" />
          </div>
          <span>Availability</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
       


          {coach.user_availabilities && coach.user_availabilities.map((item, idx) => {
            return (
              <div key={idx} className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">{item.day}</p>
                  <p className="text-xs font-medium text-gray-500 tracking-wide">{item.is_open ? "Available" : "Unavailable"}</p>
                </div>
                {item.availability_times && item.availability_times.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {item.availability_times.map((time, idx) => {
                      const start_time = moment(time.start_time).format("HH:mm");
                      const end_time = moment(time.end_time).format("HH:mm");
                      return (
                        <div key={idx} className="px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50">
                          <p className="text-xs font-semibold text-gray-900 text-center whitespace-nowrap">{start_time + " - " + end_time}</p>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-sm font-semibold text-gray-400 italic leading-relaxed">No time slots</p>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  );
};

export default CoachDetail;

   {/* {coach.user_availabilities && Object.entries(coach.user_availabilities).map(([day, data]) => (
            <div key={day} className="rounded-lg border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{day.charAt(0).toUpperCase() + day.slice(1) + " (" + (data.day ? "Available" : "Unavailable") + ")"}</p>
              {data.time_slots && data.time_slots.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {data.time_slots.map((slot, idx) => (
                    <div key={idx} className="px-2 py-1.5 rounded-lg border border-gray-200 bg-gray-50">
                      <p className="text-xs font-semibold text-gray-900 text-center whitespace-nowrap">{slot.time_slot + " - " + slot.time_slot_end}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm font-semibold text-gray-400 italic leading-relaxed">No time slots</p>
              )}
            </div>
          ))} */}